"use client";

import { useEffect, useState, useCallback, useMemo, type CSSProperties } from 'react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import BrailleSpinner from './BrailleSpinner';
import { FONT_BASTION } from "@/lib/bastion-button";

interface TimeseriesData {
  date: string;
  value: number;
}

interface ReturnsData {
  totalReturn: number;
  firstValue: number;
  lastValue: number;
  period: {
    start: string;
    end: string;
  };
}

interface PortfolioChartProps {
  portfolioCode: string;
  portfolioName: string;
  /** Outer shell min-height (default 450). Plot area scales with the shell. */
  minHeight?: number;
  /** Fill parent height (Desk split layout). Overrides fixed minHeight. */
  fill?: boolean;
}

const FONT = 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif';
const CHART_AXIS = "rgba(255,255,255,0.52)";
const CHART_META = "rgba(255,255,255,0.48)";

function fmtPct(v: number): string {
  return `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
}

function fmtAxisPct(v: number): string {
  return `${v >= 0 ? "+" : ""}${v.toFixed(0)}%`;
}

function fmtDateShort(dateStr: string): string {
  const d = parseLocalDate(dateStr);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
}

function fmtTimestampShort(ts: number): string {
  const d = new Date(ts);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
}

function fmtDateRange(start: string, end: string): string {
  return `${fmtDateShort(start)} – ${fmtDateShort(end)}`;
}

/** Parse YYYY-MM-DD as local calendar date (avoid UTC shift). */
function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Last calendar day of the month containing `dateStr`, as epoch ms. */
function monthEndTs(dateStr: string): number {
  const d = parseLocalDate(dateStr);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getTime();
}

/** Snap chart x-positions to month-end so partial months don't collapse on the axis. */
function withMonthlyChartTs<T extends { date: string; ts?: number }>(points: T[]): T[] {
  return points.map((p) => ({ ...p, ts: monthEndTs(p.date) }));
}

/** End-of-month ISO date for legend when the series ends mid-month. */
function monthEndDateStr(dateStr: string): string {
  const d = parseLocalDate(dateStr);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const y = end.getFullYear();
  const m = String(end.getMonth() + 1).padStart(2, "0");
  const day = String(end.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Keep one point per calendar month — the last chronologically. */
function keepLastPointPerMonth<T extends { date: string }>(points: T[]): T[] {
  if (points.length <= 1) return points;
  const result: T[] = [];
  for (let i = 0; i < points.length; i++) {
    const d = parseLocalDate(points[i].date);
    const monthKey = `${d.getFullYear()}-${d.getMonth()}`;
    const next = points[i + 1];
    const nextKey = next ? `${parseLocalDate(next.date).getFullYear()}-${parseLocalDate(next.date).getMonth()}` : null;
    if (monthKey !== nextKey) result.push(points[i]);
  }
  return result;
}

/** Round `range` to a "nice" 1/2/5 × 10ⁿ number. */
function niceNum(range: number, round: boolean): number {
  const exp = Math.floor(Math.log10(range));
  const frac = range / Math.pow(10, exp);
  let niceFrac: number;
  if (round) {
    if (frac < 1.5) niceFrac = 1;
    else if (frac < 3) niceFrac = 2;
    else if (frac < 7) niceFrac = 5;
    else niceFrac = 10;
  } else {
    if (frac <= 1) niceFrac = 1;
    else if (frac <= 2) niceFrac = 2;
    else if (frac <= 5) niceFrac = 5;
    else niceFrac = 10;
  }
  return niceFrac * Math.pow(10, exp);
}

/**
 * Build a tight Y-axis scale from the data's min/max so there is no big empty
 * band at the top. Baseline 0% is always included; gridlines land on round
 * numbers, and a small pad keeps the line from touching the top/bottom frame.
 */
function computeYScale(dataMin: number, dataMax: number): { domain: [number, number]; ticks: number[] } {
  const lo = Math.min(0, dataMin);
  const hi = Math.max(0, dataMax);
  const span = hi - lo || 1;
  const step = niceNum(span / 4, true);
  const niceLo = Math.floor(lo / step) * step;
  const niceHi = Math.ceil(hi / step) * step;

  const ticks: number[] = [];
  for (let v = niceLo; v <= niceHi + step * 0.5; v += step) ticks.push(Math.round(v));

  // Only pad the edge that lands exactly on the data extreme; the ceil/floor
  // already gives headroom otherwise, so we avoid re-inflating the range.
  const pad = span * 0.05;
  const domainLo = niceLo === lo ? niceLo - pad : niceLo;
  const domainHi = niceHi === hi ? niceHi + pad : niceHi;

  return { domain: [domainLo, domainHi], ticks };
}

/** Evenly-spaced month ticks for the X axis; always includes the last date. */
function buildEvenMonthTicks(dates: string[]): number[] {
  if (dates.length === 0) return [];
  if (dates.length === 1) return [new Date(dates[0]).getTime()];

  const start = parseLocalDate(dates[0]);
  const end = parseLocalDate(dates[dates.length - 1]);
  const endTs = end.getTime();

  const monthSpan =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth()) +
    1;

  let stepMonths = 2;
  if (monthSpan <= 10) stepMonths = 1;
  else if (monthSpan <= 22) stepMonths = 2;
  else if (monthSpan <= 40) stepMonths = 3;
  else stepMonths = 6;

  const ticks: number[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cur.getTime() <= endTs) {
    ticks.push(cur.getTime());
    cur.setMonth(cur.getMonth() + stepMonths);
  }

  const lastTick = ticks[ticks.length - 1];
  const lastTickDate = lastTick != null ? new Date(lastTick) : null;
  const sameMonthAsEnd =
    lastTickDate != null &&
    lastTickDate.getFullYear() === end.getFullYear() &&
    lastTickDate.getMonth() === end.getMonth();

  if (sameMonthAsEnd) {
    ticks[ticks.length - 1] = endTs;
  } else if (lastTick !== endTs) {
    ticks.push(endTs);
  }

  return ticks;
}

export default function PortfolioChart({
  portfolioCode,
  portfolioName,
  minHeight = 450,
  fill = false,
}: PortfolioChartProps) {
  const [chartData, setChartData] = useState<any[]>([]);
  const [returns, setReturns] = useState<ReturnsData | null>(null);
  const [benchmarkReturns, setBenchmarkReturns] = useState<ReturnsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const shellStyle: CSSProperties = fill
    ? { width: "100%", height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }
    : { width: "100%", height: "100%", minHeight, display: "flex", flexDirection: "column" };
  const plotMinHeight = fill ? 0 : Math.max(160, minHeight - 56);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!portfolioCode || portfolioCode === 'undefined' || portfolioCode === 'null') {
          throw new Error('Invalid portfolio code');
        }
        
        const [portfolioResponse, benchmarkResponse] = await Promise.all([
          fetch(`/api/portfolios/timeseries/${encodeURIComponent(portfolioCode)}`),
          fetch(`/api/portfolios/timeseries/14`)
        ]);
        
        if (!portfolioResponse.ok) {
          const ct = portfolioResponse.headers.get('content-type');
          let msg = '';
          if (ct?.includes('application/json')) {
            const j = await portfolioResponse.json();
            msg = j.error || JSON.stringify(j);
          } else {
            msg = await portfolioResponse.text();
          }
          throw new Error(`Portfolio data: ${portfolioResponse.status} – ${msg.substring(0, 200)}`);
        }
        
        if (!benchmarkResponse.ok) {
          const ct = benchmarkResponse.headers.get('content-type');
          let msg = '';
          if (ct?.includes('application/json')) {
            const j = await benchmarkResponse.json();
            msg = j.error || JSON.stringify(j);
          } else {
            msg = await benchmarkResponse.text();
          }
          throw new Error(`Benchmark data: ${benchmarkResponse.status} – ${msg.substring(0, 200)}`);
        }
        
        const [portfolioResult, benchmarkResult] = await Promise.all([
          portfolioResponse.json(),
          benchmarkResponse.json()
        ]);
        
        const combined = withMonthlyChartTs(
          keepLastPointPerMonth(
            portfolioResult.timeseries.map((item: TimeseriesData, i: number) => ({
              date: item.date,
              portfolio: ((item.value - 100) / 100) * 100,
              benchmark: benchmarkResult.timeseries[i]
                ? ((benchmarkResult.timeseries[i].value - 100) / 100) * 100
                : 0,
            })),
          ),
        );
        
        setChartData(combined);
        setReturns(
          portfolioResult.returns
            ? {
                ...portfolioResult.returns,
                period: {
                  ...portfolioResult.returns.period,
                  end: monthEndDateStr(portfolioResult.returns.period.end),
                },
              }
            : null,
        );
        setBenchmarkReturns(benchmarkResult.returns);
      } catch (err) {
        console.error('Error fetching timeseries:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    if (portfolioCode) fetchData();
  }, [portfolioCode]);

  const handleMouseMove = useCallback((state: any) => {
    if (state?.activeTooltipIndex != null) {
      setActiveIdx(state.activeTooltipIndex);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setActiveIdx(null);
  }, []);

  const pReturn = activeIdx != null && chartData[activeIdx]
    ? chartData[activeIdx].portfolio
    : returns?.totalReturn ?? null;
  const bReturn = activeIdx != null && chartData[activeIdx]
    ? chartData[activeIdx].benchmark
    : benchmarkReturns?.totalReturn ?? null;

  const dateLabel = activeIdx != null && chartData[activeIdx]
    ? fmtDateShort(chartData[activeIdx].date)
    : returns
      ? fmtDateRange(returns.period.start, returns.period.end)
      : null;

  const xAxisTicks = useMemo(
    () => buildEvenMonthTicks(chartData.map((d) => monthEndDateStr(d.date))),
    [chartData],
  );

  const yScale = useMemo(() => {
    if (!chartData.length) return null;
    let min = Infinity;
    let max = -Infinity;
    for (const d of chartData) {
      for (const v of [d.portfolio, d.benchmark]) {
        if (typeof v === "number" && Number.isFinite(v)) {
          if (v < min) min = v;
          if (v > max) max = v;
        }
      }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
    return computeYScale(min, max);
  }, [chartData]);

  const placeholder = (content: React.ReactNode) => (
    <div style={{ ...shellStyle, alignItems: "center", justifyContent: "center" }}>
      {content}
    </div>
  );

  if (loading) return placeholder(<BrailleSpinner size={28} label="Loading portfolio…" />);
  if (error) return placeholder(<span style={{ color: "#ef4444", fontSize: 13, fontFamily: FONT }}>{error}</span>);
  if (!chartData.length) return placeholder(<span style={{ color: "rgba(255,255,255,0.3)", fontSize: 13, fontFamily: FONT }}>No data available</span>);

  return (
    <div style={shellStyle}>
      {/* Legend */}
      <div style={{ padding: "6px 18px 4px", display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
        {/* Portfolio legend item */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <svg width="16" height="2" style={{ flexShrink: 0 }}><line x1="0" y1="1" x2="16" y2="1" stroke="#22c55e" strokeWidth="2" /></svg>
          <span
            style={{
              fontSize: 12,
              color: "rgba(255,255,255,0.7)",
              fontFamily: FONT,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 240,
            }}
            title={portfolioName}
          >
            {portfolioName}
          </span>
          {pReturn != null && (
            <span style={{ fontSize: 12, fontWeight: 600, color: pReturn >= 0 ? "#22c55e" : "#ef4444", fontFamily: FONT_BASTION }}>
              {fmtPct(pReturn)}
            </span>
          )}
        </div>
        {/* Benchmark legend item */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <svg width="16" height="2"><line x1="0" y1="1" x2="16" y2="1" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeDasharray="3 3" /></svg>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.62)", fontFamily: FONT }}>S&P500</span>
          {bReturn != null && (
            <span style={{ fontSize: 12, fontWeight: 600, color: bReturn >= 0 ? "rgba(255,255,255,0.85)" : "#ef4444", fontFamily: FONT_BASTION }}>
              {fmtPct(bReturn)}
            </span>
          )}
        </div>
        {/* Date range */}
        {dateLabel && (
          <span style={{ fontSize: 11, color: CHART_META, fontFamily: FONT, marginLeft: "auto" }}>
            {dateLabel}
          </span>
        )}
      </div>

      {/* Chart */}
      <div style={{ flex: 1, minHeight: plotMinHeight, position: "relative", minWidth: 0 }}>
        <div style={{ position: "absolute", top: 4, right: 4, bottom: 8, left: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            margin={{ top: 8, right: 16, bottom: 4, left: 0 }}
          >
            <defs>
              {/* Portfolio gradient fill */}
              <linearGradient id="portfolioGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22c55e" stopOpacity={0.25} />
                <stop offset="40%" stopColor="#22c55e" stopOpacity={0.08} />
                <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
              </linearGradient>
              {/* Benchmark gradient fill */}
              <linearGradient id="benchmarkGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ffffff" stopOpacity={0.08} />
                <stop offset="60%" stopColor="#ffffff" stopOpacity={0.02} />
                <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
              </linearGradient>
              {/* Glow filter for portfolio line */}
              <filter id="glowGreen" x="-10%" y="-10%" width="120%" height="120%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="blur" />
                <feColorMatrix in="blur" type="matrix" values="0 0 0 0 0.133  0 0 0 0 0.773  0 0 0 0 0.369  0 0 0 0.25 0" result="glow" />
                <feMerge>
                  <feMergeNode in="glow" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              {/* Glow filter for benchmark line */}
              <filter id="glowWhite" x="-10%" y="-10%" width="120%" height="120%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="1" result="blur" />
                <feColorMatrix in="blur" type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.12 0" result="glow" />
                <feMerge>
                  <feMergeNode in="glow" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <CartesianGrid
              horizontal={true}
              vertical={false}
              stroke="rgba(255,255,255,0.05)"
            />
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              ticks={xAxisTicks}
              tick={{ fill: CHART_AXIS, fontSize: 11, fontFamily: FONT }}
              tickFormatter={fmtTimestampShort}
              axisLine={false}
              tickLine={false}
              dy={8}
            />
            <YAxis
              tick={{ fill: CHART_AXIS, fontSize: 11, fontFamily: FONT_BASTION }}
              tickFormatter={fmtAxisPct}
              axisLine={false}
              tickLine={false}
              width={52}
              dx={-4}
              domain={yScale ? yScale.domain : ["auto", "auto"]}
              ticks={yScale ? yScale.ticks : undefined}
              allowDataOverflow={false}
            />
            <Tooltip
              content={() => null}
              cursor={{
                stroke: "rgba(255,255,255,0.15)",
                strokeWidth: 1,
                strokeDasharray: "3 3",
              }}
            />
            <Area
              type="monotone"
              dataKey="benchmark"
              stroke="rgba(255,255,255,0.4)"
              strokeWidth={1.5}
              strokeDasharray="5 4"
              fill="url(#benchmarkGrad)"
              dot={false}
              activeDot={{ r: 3, fill: "rgba(255,255,255,0.6)", stroke: "rgba(255,255,255,0.6)", strokeWidth: 0 }}
              isAnimationActive={false}
              style={{ filter: "url(#glowWhite)" }}
            />
            <Area
              type="monotone"
              dataKey="portfolio"
              stroke="#22c55e"
              strokeWidth={2}
              fill="url(#portfolioGrad)"
              dot={false}
              activeDot={{ r: 3, fill: "#22c55e", stroke: "#22c55e", strokeWidth: 0 }}
              isAnimationActive={false}
              style={{ filter: "url(#glowGreen)" }}
            />
          </AreaChart>
        </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
